import React, { useEffect, useMemo, useState } from 'react';
import { History, ArrowDownCircle, ArrowUpCircle, Users, Settings, Search, Filter, Edit2, Trash2, X, Save, UserPlus, UserMinus } from 'lucide-react';
import Pagination from './Pagination';
import type { Transaction, TransactionType } from '../types';
import { formatCurrency } from '../utils/helpers';

interface TransactionHistoryProps {
  transactions: Transaction[];
  onUpdate?: (transactionId: string, updates: Partial<Transaction>) => Promise<boolean>;
  onDelete?: (transactionId: string) => Promise<boolean>;
  isAdmin?: boolean;
}

const TransactionHistory: React.FC<TransactionHistoryProps> = ({
  transactions,
  onUpdate,
  onDelete,
  isAdmin = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<TransactionType | 'all'>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editAmount, setEditAmount] = useState<number | ''>('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(18);

  const canManage = isAdmin && !!onDelete;

  // Clean selection on data change
  useEffect(() => {
    const ids = new Set(transactions.map(t => t.id));
    setSelectedIds(prev => prev.filter(id => ids.has(id)));
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    return transactions.filter(tx => {
      const matchesSearch = searchTerm === '' ||
        tx.playerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.teamName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.performedByName?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesType = typeFilter === 'all' || tx.type === typeFilter;

      return matchesSearch && matchesType;
    });
  }, [transactions, searchTerm, typeFilter]);

  // Paginate filtered transactions
  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredTransactions.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredTransactions, currentPage, itemsPerPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, typeFilter]);

  const getTransactionIcon = (type: TransactionType) => {
    switch (type) {
      case 'PLAYER_SOLD':
        return <ArrowDownCircle className="w-5 h-5 text-green-500" />;
      case 'PLAYER_REMOVED':
        return <ArrowUpCircle className="w-5 h-5 text-orange-500" />;
      case 'TEAM_CREATED':
        return <Users className="w-5 h-5 text-blue-500" />;
      case 'BUDGET_ADJUSTED':
        return <Settings className="w-5 h-5 text-purple-500" />;
      case 'USER_CREATED':
        return <UserPlus className="w-5 h-5 text-emerald-500" />;
      case 'USER_DELETED':
        return <UserMinus className="w-5 h-5 text-rose-500" />;
      default:
        return <History className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTransactionBadge = (type: TransactionType) => {
    const styles: Record<TransactionType, string> = {
      'PLAYER_SOLD': 'bg-green-100 text-green-800',
      'PLAYER_REMOVED': 'bg-orange-100 text-orange-800',
      'TEAM_CREATED': 'bg-blue-100 text-blue-800',
      'BUDGET_ADJUSTED': 'bg-purple-100 text-purple-800',
      'USER_CREATED': 'bg-emerald-100 text-emerald-800',
      'USER_DELETED': 'bg-rose-100 text-rose-800'
    };
    const labels: Record<TransactionType, string> = {
      'PLAYER_SOLD': 'Sale',
      'PLAYER_REMOVED': 'Refund',
      'TEAM_CREATED': 'Team Created',
      'BUDGET_ADJUSTED': 'Adjustment',
      'USER_CREATED': 'User Created',
      'USER_DELETED': 'User Deleted'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[type]}`}>
        {labels[type]}
      </span>
    );
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const startEditing = (tx: Transaction) => {
    setEditingId(tx.id);
    setEditNotes(tx.notes || '');
    setEditAmount(tx.amount || '');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditNotes('');
    setEditAmount('');
  };

  const handleSaveEdit = async (txId: string) => {
    if (!onUpdate) return;
    const updates: Partial<Transaction> = { notes: editNotes };
    if (editAmount !== '') updates.amount = Number(editAmount);
    const success = await onUpdate(txId, updates);
    if (success) cancelEditing();
  };

  const handleDelete = async (txId: string) => {
    if (!onDelete) return;
    await onDelete(txId);
    setDeleteConfirmId(null);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const pageIds = paginatedTransactions.map(tx => tx.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));
  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const handleBulkDelete = async () => {
    if (!onDelete || selectedIds.length === 0) return;
    if (!showBulkConfirm) {
      setShowBulkConfirm(true);
      return;
    }
    setBulkDeleting(true);
    for (const id of selectedIds) {
      await onDelete(id);
    }
    setSelectedIds([]);
    setBulkDeleting(false);
    setShowBulkConfirm(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <History className="w-6 h-6 text-indigo-600" />
          <h2 className="text-xl font-bold text-gray-800">Transaction History</h2>
        </div>
        <div className="flex items-center gap-3">
          {canManage && (
            <div className="flex items-center gap-2">
              {showBulkConfirm && (
                <div className="flex items-center gap-2 bg-red-50 text-red-700 px-3 py-1.5 rounded-lg border border-red-200 text-sm">
                  Confirm delete {selectedIds.length}?
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                    className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => { setShowBulkConfirm(false); setBulkDeleting(false); }}
                    className="px-2 py-1 bg-white text-red-600 rounded border border-red-200 hover:bg-red-100"
                  >
                    No
                  </button>
                </div>
              )}
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting || selectedIds.length === 0}
                className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white disabled:opacity-50 hover:bg-red-700"
              >
                {bulkDeleting ? 'Deleting...' : `Delete Selected (${selectedIds.length})`}
              </button>
            </div>
          )}
          <span className="text-sm text-gray-500">{filteredTransactions.length} transactions</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search player, team, or user..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <select
            className="pl-10 pr-8 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 appearance-none bg-white"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TransactionType | 'all')}
          >
            <option value="all">All Types</option>
            <option value="PLAYER_SOLD">Sales</option>
            <option value="PLAYER_REMOVED">Refunds</option>
            <option value="TEAM_CREATED">Team Created</option>
            <option value="BUDGET_ADJUSTED">Adjustments</option>
            <option value="USER_CREATED">User Created</option>
            <option value="USER_DELETED">User Deleted</option>
          </select>
        </div>
      </div>

      {/* Transaction List */}
      <div className="space-y-3">
        {filteredTransactions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No transactions found</p>
          </div>
        ) : (
          <>
            {canManage && (
              <div className="flex items-center gap-2 text-sm text-gray-600 px-1">
                <input type="checkbox" checked={allPageSelected} onChange={toggleSelectAll} />
                <span>Select page</span>
              </div>
            )}
            {paginatedTransactions.map((tx) => (
              <div key={tx.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                {canManage && (
                  <div className="pt-1">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(tx.id)}
                      onChange={() => toggleSelect(tx.id)}
                    />
                  </div>
                )}
                <div className="flex-shrink-0 mt-1">
                  {getTransactionIcon(tx.type)}
                </div>
                <div className="flex-1 min-w-0">
                  {editingId === tx.id ? (
                    // Edit mode
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        {getTransactionBadge(tx.type)}
                        <input
                          type="number"
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value ? Number(e.target.value) : '')}
                          placeholder="Amount"
                          className="w-24 px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-500">pts</span>
                      </div>
                      <input
                        type="text"
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Notes"
                        className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-indigo-500"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEdit(tx.id)}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          <Save className="w-3 h-3" /> Save
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          <X className="w-3 h-3" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : deleteConfirmId === tx.id ? (
                    // Delete confirmation
                    <div className="space-y-2">
                      <p className="text-sm text-red-600 font-medium">Delete this transaction?</p>
                      <p className="text-xs text-gray-500">{tx.notes}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDelete(tx.id)}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                        >
                          <Trash2 className="w-3 h-3" /> Confirm Delete
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          <X className="w-3 h-3" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Normal view
                    <>
                      <div className="flex items-center space-x-2 mb-1">
                        {getTransactionBadge(tx.type)}
                        {tx.amount && (
                          <span className={`text-sm font-semibold ${tx.type === 'PLAYER_REMOVED' ? 'text-orange-600' : 'text-green-600'}`}>
                            {tx.type === 'PLAYER_REMOVED' ? '+' : '-'}{formatCurrency(tx.amount)} pts
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-800 truncate">{tx.notes}</p>
                      <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                        <span>{formatDate(tx.timestamp)}</span>
                        <span>•</span>
                        <span>by {tx.performedByName}</span>
                      </div>
                    </>
                  )}
                </div>
                {/* Edit/Delete buttons - only show for admins and when not editing */}
                {canManage && !editingId && !deleteConfirmId && (
                  <div className="flex-shrink-0 flex gap-1">
                    <button
                      onClick={() => startEditing(tx)}
                      className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                      title="Edit transaction"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirmId(tx.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete transaction"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Pagination */}
      {filteredTransactions.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <Pagination
            currentPage={currentPage}
            totalItems={filteredTransactions.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;
